// Phase 1C — real pointer/pinch gesture state machine (mouse + touch + pen).
//
// Pure state machine — no DOM, no React. GridViewer feeds pointer events into
// it; tests exercise the same functions with synthetic pointer samples.
//
// Rules (reviewer P0-1):
//  - 1 pointer = pan (world moves under the pointer's screen delta).
//  - 2 pointers = pinch: scale ratio = currDist/startDist; the world point that
//    was under the pinch-start centroid stays under the CURRENT centroid.
//    This yields simultaneous pan + zoom naturally as the centroid moves.
//  - 1→2 transition: re-anchor pinch at current camera → no visible jump.
//  - 2→1 transition: switch back to pan using the remaining pointer's current
//    position and current camera → no jump.
//  - pointerup / pointercancel cleans state.
//  - A tap is emitted only when the ENTIRE gesture used at most 1 pointer AND
//    total accumulated movement is below the tap slop. Any pinch permanently
//    kills the tap for the whole gesture.
//  - Hover (see GridViewer) is exposed only for fine pointers via callers.
import type { Camera } from "./camera";
import { screenToWorld } from "./camera";

export type PointerKind = "mouse" | "touch" | "pen";

export interface PointerInput {
  id: number;
  x: number;
  y: number;
  type?: PointerKind;
}

export const TAP_SLOP_PX = 4;

interface PanStart {
  pointerId: number;
  startX: number;
  startY: number;
  camX: number;
  camY: number;
}
interface PinchStart {
  worldAtCentroid: { x: number; y: number };
  startDistance: number;
  startScale: number;
}

export interface GestureState {
  active: Map<number, { x: number; y: number }>;
  mode: "idle" | "pan" | "pinch";
  panStart: PanStart | null;
  pinchStart: PinchStart | null;
  totalMoved: number;
  wasPinch: boolean;
  downX: number;
  downY: number;
}

export function initGesture(): GestureState {
  return {
    active: new Map(),
    mode: "idle",
    panStart: null,
    pinchStart: null,
    totalMoved: 0,
    wasPinch: false,
    downX: 0,
    downY: 0,
  };
}

function dist(a: { x: number; y: number }, b: { x: number; y: number }): number {
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  return Math.hypot(dx, dy);
}
function centroid(pts: { x: number; y: number }[]): { x: number; y: number } {
  let sx = 0;
  let sy = 0;
  for (const p of pts) {
    sx += p.x;
    sy += p.y;
  }
  return { x: sx / pts.length, y: sy / pts.length };
}
function firstTwo(m: Map<number, { x: number; y: number }>): { x: number; y: number }[] {
  const arr: { x: number; y: number }[] = [];
  for (const v of m.values()) {
    arr.push(v);
    if (arr.length === 2) break;
  }
  return arr;
}

export interface MoveResult {
  camera?: Camera;
}
export interface UpResult {
  camera?: Camera;
  tap?: { sx: number; sy: number };
}

export function onPointerDown(state: GestureState, p: PointerInput, camera: Camera): GestureState {
  const wasEmpty = state.active.size === 0;
  state.active.set(p.id, { x: p.x, y: p.y });
  if (wasEmpty) {
    state.downX = p.x;
    state.downY = p.y;
    state.totalMoved = 0;
    state.wasPinch = false;
  }
  if (state.active.size === 1) {
    state.mode = "pan";
    state.panStart = {
      pointerId: p.id,
      startX: p.x,
      startY: p.y,
      camX: camera.x,
      camY: camera.y,
    };
    state.pinchStart = null;
  } else {
    // 2+ pointers: enter/re-anchor pinch at CURRENT camera — no jump on 1→2.
    state.wasPinch = true;
    const pts = firstTwo(state.active);
    const c = centroid(pts);
    state.pinchStart = {
      worldAtCentroid: screenToWorld(camera, c.x, c.y),
      startDistance: Math.max(1e-6, dist(pts[0], pts[1])),
      startScale: camera.scale,
    };
    state.mode = "pinch";
    state.panStart = null;
  }
  return state;
}

export function onPointerMove(
  state: GestureState,
  p: PointerInput,
  camera: Camera,
): MoveResult {
  const prev = state.active.get(p.id);
  if (!prev) return {};
  state.totalMoved += Math.abs(p.x - prev.x) + Math.abs(p.y - prev.y);
  state.active.set(p.id, { x: p.x, y: p.y });

  if (state.mode === "pan" && state.panStart) {
    const dxT = (p.x - state.panStart.startX) / camera.scale;
    const dyT = (p.y - state.panStart.startY) / camera.scale;
    return {
      camera: {
        x: state.panStart.camX - dxT,
        y: state.panStart.camY - dyT,
        scale: camera.scale,
      },
    };
  }
  if (state.mode === "pinch" && state.pinchStart && state.active.size >= 2) {
    const pts = firstTwo(state.active);
    const c = centroid(pts);
    const d = Math.max(1e-6, dist(pts[0], pts[1]));
    const ratio = d / state.pinchStart.startDistance;
    const newScale = state.pinchStart.startScale * ratio;
    return {
      camera: {
        scale: newScale,
        x: state.pinchStart.worldAtCentroid.x - c.x / newScale,
        y: state.pinchStart.worldAtCentroid.y - c.y / newScale,
      },
    };
  }
  return {};
}

export function onPointerUp(
  state: GestureState,
  p: PointerInput,
  camera: Camera,
): UpResult {
  if (!state.active.has(p.id)) return {};
  state.active.delete(p.id);

  if (state.active.size === 0) {
    const shouldTap = !state.wasPinch && state.totalMoved < TAP_SLOP_PX;
    const tap = shouldTap ? { sx: state.downX, sy: state.downY } : undefined;
    state.mode = "idle";
    state.panStart = null;
    state.pinchStart = null;
    return tap ? { tap } : {};
  }
  if (state.active.size === 1) {
    // 2→1: resume pan using remaining pointer + CURRENT camera → no jump.
    const [rid, rp] = Array.from(state.active.entries())[0];
    state.mode = "pan";
    state.pinchStart = null;
    state.panStart = {
      pointerId: rid,
      startX: rp.x,
      startY: rp.y,
      camX: camera.x,
      camY: camera.y,
    };
    return {};
  }
  // Still 2+ pointers: remain in pinch, re-anchor at current camera.
  const pts = firstTwo(state.active);
  const c = centroid(pts);
  state.pinchStart = {
    worldAtCentroid: screenToWorld(camera, c.x, c.y),
    startDistance: Math.max(1e-6, dist(pts[0], pts[1])),
    startScale: camera.scale,
  };
  return {};
}

export function onPointerCancel(state: GestureState): GestureState {
  state.active.clear();
  state.mode = "idle";
  state.panStart = null;
  state.pinchStart = null;
  state.totalMoved = 0;
  state.wasPinch = false;
  return state;
}
